"use client";

import {
  ChevronRight,
  ContactRound,
  Copy,
  FileQuestion,
  FileText,
  Home,
  LayoutGrid,
  PackageSearch,
  Plus,
  Search,
  ShoppingCart,
  Trash2,
  UserRound,
  WalletCards,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type BuilderPageLibraryKind = "pages" | "systemPages";

export type BuilderPageLibraryItem = {
  canDelete?: boolean;
  canDuplicate?: boolean;
  draft: boolean;
  id: string;
  languageCode: string;
  pageType: string;
  slug: string;
  title: string;
  unavailable: boolean;
  visible: boolean;
};

export type BuilderPageLibraryLabels = {
  available: string;
  backToEditor: string;
  create: string;
  delete: string;
  description: Record<BuilderPageLibraryKind, string>;
  draft: string;
  duplicate: string;
  empty: string;
  eyebrow: string;
  hidden: string;
  languageUnavailable: string;
  pageCount: (count: number) => string;
  published: string;
  title: Record<BuilderPageLibraryKind, string>;
  visible: string;
};

export type BuilderPageLibraryProps = {
  activeKind: BuilderPageLibraryKind;
  busy: boolean;
  contentPages: readonly BuilderPageLibraryItem[];
  labels: BuilderPageLibraryLabels;
  onBack: () => void;
  onCreatePage?: () => void;
  onDeletePage?: (pageId: string) => void;
  onDuplicatePage?: (pageId: string) => void;
  onKindChange: (kind: BuilderPageLibraryKind) => void;
  onOpenPage: (pageId: string, kind: BuilderPageLibraryKind) => void;
  selectedPageId: string | null;
  systemPages: readonly BuilderPageLibraryItem[];
};

const pageIcons: Record<string, LucideIcon> = {
  cart: ShoppingCart,
  category: LayoutGrid,
  checkout: WalletCards,
  contact: ContactRound,
  customerAccount: UserRound,
  home: Home,
  notFound: FileQuestion,
  product: PackageSearch,
  search: Search,
  section: LayoutGrid,
};

export function BuilderPageLibrary({
  activeKind,
  busy,
  contentPages,
  labels,
  onBack,
  onCreatePage,
  onDeletePage,
  onDuplicatePage,
  onKindChange,
  onOpenPage,
  selectedPageId,
  systemPages,
}: BuilderPageLibraryProps) {
  const pages = activeKind === "pages" ? contentPages : systemPages;

  return (
    <div
      className="min-h-full bg-[var(--app-canvas-color)] px-3 py-5 text-text sm:px-6 sm:py-8 lg:px-8"
      data-builder-page-library="true"
      data-builder-page-library-kind={activeKind}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-2xl">
            <p className="text-xs font-semibold text-text-muted">{labels.eyebrow}</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              {labels.title[activeKind]}
            </h1>
            <p className="mt-2 text-sm leading-6 text-text-secondary">
              {labels.description[activeKind]}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn-base btn-secondary min-h-10" onClick={onBack}>
              {labels.backToEditor}
            </button>
            {activeKind === "pages" && onCreatePage ? (
              <button
                type="button"
                className="btn-base btn-primary min-h-10"
                disabled={busy}
                onClick={onCreatePage}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {labels.create}
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto" aria-label={labels.eyebrow}>
          {(["pages", "systemPages"] as const).map((kind) => (
            <button
              aria-pressed={activeKind === kind}
              className={[
                "flex min-h-12 shrink-0 items-center gap-2 rounded-t-2xl border-b-2 px-4 text-sm font-semibold transition-colors sm:px-6",
                "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary motion-reduce:transition-none",
                activeKind === kind
                  ? "border-primary bg-surface text-primary"
                  : "border-transparent text-text-secondary hover:bg-surface-hover hover:text-text",
              ].join(" ")}
              key={kind}
              onClick={() => onKindChange(kind)}
              type="button"
            >
              <span>{labels.title[kind]}</span>
              <span className={[
                "min-w-5 rounded-full px-1.5 py-0.5 text-[11px] leading-4",
                activeKind === kind ? "bg-primary/10 text-primary" : "bg-bg-sunken text-text-muted",
              ].join(" ")}>
                {kind === "pages" ? contentPages.length : systemPages.length}
              </span>
            </button>
          ))}
        </div>

        <div className="rounded-b-3xl rounded-tr-3xl bg-surface p-3 sm:rounded-b-[32px] sm:rounded-tr-[32px] sm:p-5">
          <div className="flex min-h-12 items-center justify-between gap-3 rounded-t-2xl bg-bg-sunken/80 px-3 py-3 text-xs text-text-secondary sm:px-4">
            <h2 className="font-medium">{labels.available}</h2>
            <span>{labels.pageCount(pages.length)}</span>
          </div>

          {pages.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {pages.map((page) => {
                const selected = selectedPageId === page.id;
                const Icon = pageIcons[page.pageType] ?? FileText;

                return (
                  <li
                    className={[
                      "group flex min-w-0 flex-wrap items-center rounded-xl transition-colors motion-reduce:transition-none",
                      selected ? "bg-primary/5" : "hover:bg-surface-hover/70",
                    ].join(" ")}
                    data-builder-page-library-item={page.id}
                    key={page.id}
                  >
                    <button
                      aria-label={page.title}
                      aria-current={selected ? "page" : undefined}
                      className="flex min-h-20 min-w-0 flex-1 items-center gap-3 rounded-xl px-3 py-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 sm:gap-4 sm:px-4"
                      disabled={busy || page.unavailable}
                      onClick={() => onOpenPage(page.id, activeKind)}
                      type="button"
                    >
                      <span className={[
                        "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
                        selected ? "bg-primary/15 text-primary" : "bg-bg-sunken text-text-secondary",
                      ].join(" ")}>
                        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-primary">{page.title}</span>
                          <span className="mt-0.5 block truncate text-xs text-text-muted" title={page.slug}>
                            /{page.slug || page.pageType}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-wrap items-center gap-1.5 text-[11px] font-medium">
                          {page.unavailable ? (
                            <span className="rounded-full bg-warning/10 px-2 py-0.5 text-warning">{labels.languageUnavailable}</span>
                          ) : (
                            <>
                              <span className={[
                                "rounded-full px-2 py-0.5",
                                page.visible ? "bg-success/10 text-success" : "bg-bg-sunken text-text-muted",
                              ].join(" ")}>
                                {page.visible ? labels.visible : labels.hidden}
                              </span>
                              <span className={[
                                "rounded-full px-2 py-0.5",
                                page.draft ? "bg-warning/10 text-warning" : "bg-primary/10 text-primary",
                              ].join(" ")}>
                                {page.draft ? labels.draft : labels.published}
                              </span>
                              <span className="rounded-md bg-bg-sunken px-1.5 py-0.5 text-text-muted">
                                {page.languageCode.toUpperCase()}
                              </span>
                            </>
                          )}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    </button>
                    {((page.canDuplicate && onDuplicatePage) || (page.canDelete && onDeletePage)) ? (
                      <div className="flex shrink-0 items-center gap-0.5 pr-2 sm:pr-3">
                        {page.canDuplicate && onDuplicatePage ? (
                          <button
                            type="button"
                            className="grid h-10 w-10 place-items-center rounded-xl text-text-muted transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-40"
                            aria-label={labels.duplicate}
                            title={labels.duplicate}
                            disabled={busy}
                            onClick={() => onDuplicatePage(page.id)}
                          >
                            <Copy className="h-4 w-4" aria-hidden="true" />
                          </button>
                        ) : null}
                        {page.canDelete && onDeletePage ? (
                          <button
                            type="button"
                            className="grid h-10 w-10 place-items-center rounded-xl text-text-muted transition-colors hover:bg-danger/10 hover:text-danger focus-visible:outline-2 focus-visible:outline-danger disabled:opacity-40"
                            aria-label={labels.delete}
                            title={labels.delete}
                            disabled={busy}
                            onClick={() => onDeletePage(page.id)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-center gap-3 px-4 py-14 text-center text-sm text-text-muted">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-bg-sunken text-text-secondary">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>
              {labels.empty}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
